// src/stores/StraitScriptStore.js
// =============================================================
// v11 step 3d — 滾動觸發大腦
// 模仿日本大選 TriggerScriptStore.js 的架構，但改成「動態產生」：
// 從 ContentDataStore (ArchieML JSON) + straitScenes.js 合併出三個陣列。
//
// 跟原版的差異:
//  - 原版:8 縣手寫死,每縣一個 onEnter/onLeave 物件,readable 匯出
//  - 七海咽喉:海峽 N 個 full_content,必須動態產生,writable 匯出
//
// 資料分工(step 2 確立):
//  - layers 顯示控制 → ArchieML 每個 step 的 visible_layers 欄位(編輯填)
//  - bbox / 圖層定義 / pathTransitions → straitScenes.js(工程師填)
//
// ArchieML step 可選欄位:
//  - bbox_override: "west,south|east,north" → 該 step 進場時 flyToBbox 用這個 bbox
//  - strait_id_override: scene_id            → 該 step 進場時切到指定 scene(用於合併 scene)
//
// 動作對應(step 3d 揭幕重組):
//  - introStepInfo[i]      → 拉上深色黑布 + 顯示大標(地圖此時看不到,所以不切 scene/layer/bbox)
//  - pathTransitionStep    → 黑布底下靜默切 scene + layers + bbox,完成後拉開黑布(揭幕!)
//  - fullContentStepInfo[i]→ setVisibleLayers + showFullBoxBg (鏡頭已在前一個 path_transition 就位)
//
// 編輯規範(方案 P):
//  - intro 後面必須跟 path_transition(不然黑布拉不開)
//  - full_content 前面必須有 path_transition,例外:前一個是 intro 且自己沒 bbox_override
// =============================================================
import { writable, get } from 'svelte/store'
import { straitSceneInfo, sceneProgress, currentSceneId } from './SceneDataStore.js'
import ContentDataStore from './ContentDataStore.js'
import { straitScenes, sceneOrder, normalizeVisibleLayers, getSceneBbox } from '../data/straitScenes.js'
import { isMobile } from './DeviceDetectorStore.js'

// =============================================================
// 訂閱地圖方法(由 StraitMap.svelte 在 onMount 註冊進來)
// 跟日本原版 jpInfo 取得方式一致
// =============================================================
let mapInfo
straitSceneInfo.subscribe((data) => {
  mapInfo = data
})

// =============================================================
// 三個 writable 陣列(初始空陣列,等內容載入後填入)
// 命名沿用日本原版,讓 StraitMapStepScroller 改寫時 import 名字不變
// =============================================================
const introStepInfo = writable([])
const fullContentStepInfo = writable([])
const emptyBoxStepInfo = writable([])

// =============================================================
// 進度更新 helper(對應原版 updateSceneProgress)
// =============================================================
function updateSceneProgress(idx) {
  sceneProgress.update(() => idx)
}

// =============================================================
// 白遮罩控制(決策 B 拍板:照原版用 querySelector 戳 DOM)
// =============================================================
function showFullBoxBg() {
  const el = document.querySelector('#full-box-bg')
  if (el) el.style.opacity = 0.98
}
function hideFullBoxBg() {
  const el = document.querySelector('#full-box-bg')
  if (el) el.style.opacity = 0
}

// =============================================================
// 黑布控制(step 3d):
//   intro.onEnter 拉上(蓋住地圖,讀者只看到大標)
//   下一個 path_transition.onEnter 拉開(揭幕!地圖出現)
// =============================================================
function showIntroCurtain() {
  const el = document.querySelector('#intro-curtain')
  if (el) el.style.opacity = 1
}
function hideIntroCurtain() {
  const el = document.querySelector('#intro-curtain')
  if (el) el.style.opacity = 0
}

// =============================================================
// bbox_override 解析(step 3):
//   ArchieML 格式:"west,south|east,north" 例如 "120.5,23.2|122.8,25.6"
//   解析成 [[west, south], [east, north]] 餵給 flyToBbox
//   無效 / 空字串 → 回 null,呼叫端 fallback 用 getSceneBbox(scene, isMobile)
//   [v15] 編輯者填的絕對座標不分裝置;若要區分桌機/手機,留空讓 scene 預設處理
// =============================================================
function parseBboxOverride(raw) {
  if (!raw || typeof raw !== 'string') return null
  const parts = raw.split('|').map(s => s.trim())
  if (parts.length !== 2) return null
  const sw = parts[0].split(',').map(Number)
  const ne = parts[1].split(',').map(Number)
  if (sw.length !== 2 || ne.length !== 2) return null
  if (sw.some(isNaN) || ne.some(isNaN)) return null
  return [[sw[0], sw[1]], [ne[0], ne[1]]]
}

// =============================================================
// 核心:從 ContentDataStore + straitScenes 動態產生三個陣列
// 索引規則:全域索引,跟 DOM 順序對齊(看 StraitMapStepScroller 的 forEach)
// =============================================================
function buildScriptArrays(contentData) {
  const intros = []
  const fulls = []
  const empties = []

  // [v15] 桌機/手機分版 bbox:取一次 snapshot 整個函式共用
  //   實務上 ContentDataStore 載入時取一次即可,使用者中途 resize 視窗
  //   不會 reflow 已建好的 handler closure (同 StraitMap.svelte 的取捨)
  const isMobileNow = get(isMobile)

  // 全域 sceneProgress 索引,每進一個 intro 加 1(跟原版每縣 +1 一致)
  let sceneProgressIdx = 0

  // ⭐ step-onleave:預先把所有 full_content 攤平,計算每張的「next/prev full 狀態」
  // 讓 onLeave / onLeaveBack 可以從閉包直接拿到鄰居資訊,不需要再走索引。
  // 鄰居是「跨 scene 的全域順序」——也就是讀者實際滾動時看到的下一張/上一張 full。
  // step-strait-curtain:同時計算「我跟下一張 full 之間,中間有沒有 intro」
  //   → 有 intro:onLeave 不切場景,改拉黑幕(海峽 → 海峽 過渡)
  //   → 沒 intro:onLeave 照常切到下一張 full 的狀態(同 scene 多張 full、合併 scene 過渡)
  const flatFulls = []
  // 暫存:每張 full 在原始 story 裡的位置,用來判斷「中間有沒有 intro」
  const flatFullsRawPos = [] // { straitId, idx in story }
  for (const straitId of sceneOrder) {
    const story = contentData[`scroll_${straitId}`]
    const sceneData = straitScenes[straitId]
    if (!story || !sceneData) continue
    for (let storyIdx = 0; storyIdx < story.length; storyIdx++) {
      const item = story[storyIdx]
      if (item.type === 'full_content') {
        const targetStraitId = item.strait_id_override || straitId
        const targetSceneData = straitScenes[targetStraitId] || sceneData
        flatFulls.push({
          item,
          straitId, // ArchieML 所屬海峽
          sceneData,
          targetStraitId, // 實際要顯示的 scene id (handle strait_id_override)
          targetSceneData,
          layers: normalizeVisibleLayers(item.visible_layers),
          bbox: parseBboxOverride(item.bbox_override) || getSceneBbox(targetSceneData, isMobileNow),
          // ⭐ step-strait-curtain:這個 flag 等下一輪掃描補上
          hasIntroBeforeNextFull: false,
        })
        flatFullsRawPos.push({ straitId, storyIdx })
      }
    }
  }
  // 第二輪掃描:給每張 full 算 hasIntroBeforeNextFull
  // 規則:從本張 full 之後的所有元素(跨 scene)往下掃,直到遇到下一個 full_content
  //       過程中如果經過 intro,就標記 true
  for (let i = 0; i < flatFulls.length - 1; i++) {
    const here = flatFullsRawPos[i]
    const next = flatFullsRawPos[i + 1]
    let foundIntro = false
    // 從 here 之後一路掃到 next 之前
    let curStraitIdx = sceneOrder.indexOf(here.straitId)
    let curStoryIdx = here.storyIdx + 1
    while (curStraitIdx < sceneOrder.length) {
      const story = contentData[`scroll_${sceneOrder[curStraitIdx]}`] || []
      while (curStoryIdx < story.length) {
        const it = story[curStoryIdx]
        // 到下一張 full,停止掃描
        if (sceneOrder[curStraitIdx] === next.straitId && curStoryIdx === next.storyIdx) {
          break
        }
        if (it.type === 'intro') foundIntro = true
        curStoryIdx++
      }
      // 已到 next,結束
      if (sceneOrder[curStraitIdx] === next.straitId && curStoryIdx === next.storyIdx) break
      // 換下一個 scroll
      curStraitIdx++
      curStoryIdx = 0
    }
    flatFulls[i].hasIntroBeforeNextFull = foundIntro
  }

  function fullStateAt(idx) {
    if (idx < 0 || idx >= flatFulls.length) return null
    return flatFulls[idx]
  }

  for (const straitId of sceneOrder) {
    const story = contentData[`scroll_${straitId}`]
    const sceneData = straitScenes[straitId]
    if (!story || !sceneData) continue

    // 每個海峽的 path_transition 索引,用來查 sceneData.pathTransitions 對應的 from/to bbox
    // (full_content 不需要計數:layers 直接從 ArchieML 物件 item.visible_layers 讀)
    let pathTransitionIdxInScene = 0

    for (const item of story) {
      if (item.type === 'intro') {
        sceneProgressIdx += 1
        const myIdx = sceneProgressIdx // 閉包鎖住

        // 編輯規範:intro 後面必須有 path_transition,不然黑布拉不開
        const itemIdx = story.indexOf(item)
        const nextItem = story[itemIdx + 1]
        const hasPathTransitionAfter = nextItem && nextItem.type === 'path_transition'
        if (!hasPathTransitionAfter) {
          console.warn(
            `[StraitScriptStore] intro "${item.content || straitId}" `
            + `(in scroll_${straitId}) 後面缺少 path_transition,黑布永遠不會拉開!`
            + `請在 ArchieML 加一個 path_transition spacer。`
          )
        }

        intros.push({
          onEnter() {
            // step 3d:intro 階段不切地圖,只拉黑布 + 顯示大標
            // 地圖切換動作搬到下一個 path_transition.onEnter
            hideFullBoxBg() // 防護:讀者從上個 full 過來時白遮罩可能還在
            showIntroCurtain() // ★ 拉上黑布,蓋住背景地圖
            updateSceneProgress(myIdx)
            currentSceneId.set(straitId)
          },
          onLeave() {
            // 空(C 案;黑布拉開由下一個 path_transition 負責)
          },
          onEnterBack() {
            hideFullBoxBg()
            showIntroCurtain() // 同上,往回滾進 intro 也要拉黑布
            currentSceneId.set(straitId)
          },
          onLeaveBack() {
            // 空(C 案)
          },
        })
      }

      else if (item.type === 'full_content') {
        const layersForThisFull = normalizeVisibleLayers(item.visible_layers)
        const targetStraitId = item.strait_id_override || straitId
        const targetSceneData = straitScenes[targetStraitId] || sceneData
        const bboxForThisFull = parseBboxOverride(item.bbox_override) || getSceneBbox(targetSceneData, isMobileNow)

        // ---- 編輯規範檢查(方案 P 精確版) ----
        // full_content 前面必須有 path_transition 讓鏡頭飛行
        // 例外:前一個是 intro 且自己沒有 bbox_override(intro 的 flyToBbox 已就位)
        const prevItem = story[story.indexOf(item) - 1]
        const isIntroNoOverride =
          prevItem && prevItem.type === 'intro' && !item.bbox_override
        const hasPathTransitionBefore =
          prevItem && prevItem.type === 'path_transition'
        const needsFlyOnEnter = !hasPathTransitionBefore && !isIntroNoOverride

        if (needsFlyOnEnter) {
          console.warn(
            `[StraitScriptStore] full_content "${item.title || '(untitled)"'}" `
            + `(in scroll_${straitId}) 前面缺少 path_transition,鏡頭飛行沒有滾動空間。`
            + `請在 ArchieML 加一個 path_transition spacer。`
          )
        }

        // ⭐ step-onleave:把這張 full 在 flatFulls 裡的索引算出來,
        // 用來在 onLeave / onLeaveBack 抓鄰居資訊
        const myFlatIdx = fulls.length // fulls 的長度跟 flatFulls 同步遞增

        // ⭐ step-early:80% 早切的 flag,onEnter / onEnterBack 重置
        // 「跨 scene 的 setActiveStraitId」是唯一會被提早觸發的動作
        // (其他動作 — flyToBbox / setVisibleLayers — 都留在 onLeave,讀者要看到)
        const EARLY_SCENE_SWITCH_PROGRESS = 0.8
        let earlySceneSwitchedFlag = false

        fulls.push({
          onEnter() {
            earlySceneSwitchedFlag = false // 重置 early 旗標
            // 切 scene(白遮罩底下靜默切,合併 scene 才會真的切)
            // 注意:大多數情況 scene 已經由前一個 path_transition / 上一張 full 的 onLeave 切好,
            // 這裡的 setActiveStraitId 是「同 scene no-op」,只在邊界(第一張 full、reload 等)真的切換
            mapInfo?.setActiveStraitId?.(targetStraitId)
            // 降級保護:沒前置 path_transition 時自己 flyToBbox(避免 bug)
            if (needsFlyOnEnter) {
              mapInfo?.flyToBbox?.(bboxForThisFull)
            }
            // 切 layer:大多數情況也是 no-op(layer 已由上一張的 onLeave 切好),
            // 此處保留是為了「路徑強健性」——避免在邊界情況下 layer 沒設到位
            mapInfo?.setVisibleLayers?.(layersForThisFull)
            showFullBoxBg()
          },
          onUpdate(progress) {
            // ⭐ step-early:讀者讀到 80% 處時,在白遮罩底下偷偷切 scene
            // 只在跨 scene 的時候才有效用(同 scene 是 no-op,但呼叫無害)
            // ⭐ step-strait-curtain:若下一張 full 之前有 intro(海峽過渡),
            //   就不該在這裡偷切——因為 onLeave 會改成拉黑幕,場景切換要等揭幕時
            if (progress >= EARLY_SCENE_SWITCH_PROGRESS && !earlySceneSwitchedFlag) {
              earlySceneSwitchedFlag = true
              const myState = fullStateAt(myFlatIdx)
              const next = fullStateAt(myFlatIdx + 1)
              if (myState && !myState.hasIntroBeforeNextFull
                  && next && next.targetStraitId !== targetStraitId) {
                // 白遮罩此時 0.95 還滿,讀者看不到 scene fade,等 onLeave 才會看到
                mapInfo?.setActiveStraitId?.(next.targetStraitId)
              }
            }
          },
          onLeave() {
            // ⭐ 關鍵改動:讀者滾出這張 full,馬上開始切換到「下一張 full」的狀態
            // step-strait-curtain:若下一張 full 之前有 intro,改成拉黑幕(海峽 → 海峽 過渡)
            // 黑幕拉上後,讀者不會看到任何地圖變化,所以不切 scene/layer/bbox。
            // 場景切換留給「intro 後的 path_transition.onEnter」做(原本就會做)。
            hideFullBoxBg()

            const myState = fullStateAt(myFlatIdx)
            const nextFullState = fullStateAt(myFlatIdx + 1)

            if (myState && myState.hasIntroBeforeNextFull) {
              // 海峽 → 海峽 過渡:拉黑幕,等 intro 後的 path_transition 揭幕
              showIntroCurtain()
              return
            }

            if (nextFullState) {
              // 切 scene(若跨 scene。冪等保護:onUpdate 可能已經切過,這裡是 no-op)
              if (nextFullState.targetStraitId !== targetStraitId) {
                mapInfo?.setActiveStraitId?.(nextFullState.targetStraitId)
              }
              // 飛到下一張的 bbox(若不同)
              mapInfo?.flyToBbox?.(nextFullState.bbox)
              // 切到下一張的 layer
              mapInfo?.setVisibleLayers?.(nextFullState.layers)
            }
            // 若 nextFullState 是 null(整篇最後一張 full),什麼都不做
          },
          onEnterBack() {
            earlySceneSwitchedFlag = false // 重置 early 旗標
            // ⭐ step-strait-curtain:從下方海峽往上滾回本張 full 時,黑幕可能還在(因為下一張過渡時拉了)
            //   需要關掉,讓讀者看回本 scene 的地圖
            hideIntroCurtain()
            mapInfo?.setActiveStraitId?.(targetStraitId)
            if (needsFlyOnEnter) {
              mapInfo?.flyToBbox?.(bboxForThisFull)
            }
            mapInfo?.setVisibleLayers?.(layersForThisFull)
            showFullBoxBg()
          },
          onLeaveBack() {
            // ⭐ 對稱處理:讀者往上滾出這張 full,要切回「上一張 full」的狀態
            hideFullBoxBg()

            const prevFullState = fullStateAt(myFlatIdx - 1)
            if (prevFullState) {
              if (prevFullState.targetStraitId !== targetStraitId) {
                mapInfo?.setActiveStraitId?.(prevFullState.targetStraitId)
              }
              mapInfo?.flyToBbox?.(prevFullState.bbox)
              mapInfo?.setVisibleLayers?.(prevFullState.layers)
            }
          },
        })
      }

      else if (item.type === 'path_transition') {
        // step 3d:path_transition 負責「揭幕」(若前面是 intro)
        // step-onleave:full → full 場景下,scene/layer/bbox 切換已搬到 full.onLeave,
        //               所以這個 path_transition 是純滾動 spacer,什麼都不做
        const itemIdx = story.indexOf(item)
        const prevItem = story[itemIdx - 1]
        const isAfterIntro = prevItem && prevItem.type === 'intro'

        // 揭幕用:找下一張 full 算它的 scene / layers / bbox
        let nextFull = null
        for (let j = itemIdx + 1; j < story.length; j++) {
          if (story[j].type === 'full_content') { nextFull = story[j]; break }
        }
        let targetBbox = null
        let targetStraitIdForFly = straitId
        let targetLayers = []
        if (nextFull) {
          targetStraitIdForFly = nextFull.strait_id_override || straitId
          const nextSceneData = straitScenes[targetStraitIdForFly] || sceneData
          targetBbox = parseBboxOverride(nextFull.bbox_override) || getSceneBbox(nextSceneData, isMobileNow)
          targetLayers = normalizeVisibleLayers(nextFull.visible_layers)
        }

        // 保留 step3 原本的 interpolateBbox 邏輯(scene 內部跟著進度連續變焦)
        const transition = sceneData.pathTransitions?.[pathTransitionIdxInScene]
        pathTransitionIdxInScene += 1

        empties.push({
          onEnter() {
            if (isAfterIntro) {
              // 黑布底下靜默切 scene + layer + bbox,然後拉開黑布揭幕
              mapInfo?.setActiveStraitId?.(targetStraitIdForFly)
              mapInfo?.setVisibleLayers?.(targetLayers)
              if (targetBbox) {
                mapInfo?.flyToBbox?.(targetBbox)
              }
              hideIntroCurtain() // ★ 拉開黑布!揭幕
            }
            // full → full:空轉(切換邏輯已在上一張 full 的 onLeave 觸發)
          },
          onLeave() {
            // 空(C 案)
          },
          onEnterBack() {
            // 往回滾進這個 path_transition:
            // 若這個 path_transition 是「intro 之後的揭幕區」,往回滾要重新拉黑布
            if (isAfterIntro) {
              showIntroCurtain()
            }
            // full → full:空轉(還原邏輯已在下方 full 的 onLeaveBack 觸發)
          },
          onLeaveBack() {
            // 空
          },
          onUpdate(progress) {
            // 進階用法:scene 內部跟著進度連續變焦(目前流程不使用)
            if (!transition) return
            mapInfo?.interpolateBbox?.(transition.fromBbox, transition.toBbox, progress)
          },
        })
      }

      // 其他 type(例如未來新增的)忽略,不報錯
    }
  }

  return { intros, fulls, empties }
}

// =============================================================
// 訂閱 ContentDataStore,內容載入後填一次三個陣列
// (採 v10 對話拍板的 B 案:writable + 訂閱)
// =============================================================
let scriptArraysBuilt = false
ContentDataStore.subscribe((contentData) => {
  if (!contentData || scriptArraysBuilt) return
  const { intros, fulls, empties } = buildScriptArrays(contentData)
  introStepInfo.set(intros)
  fullContentStepInfo.set(fulls)
  emptyBoxStepInfo.set(empties)
  scriptArraysBuilt = true
})

export { introStepInfo, fullContentStepInfo, emptyBoxStepInfo }
